/**
 * What sits behind each inventory category — activities and reports.
 *
 * CONFIDENTIAL — ASI Internal.
 * Copyright 2026, Partnership Net; Agency for Sponsorship Intelligence, Lda;
 * Advanced Sponsorship Insights, GmbH. Confidential and proprietary.
 *
 * No React, no DOM. Deterministic throughout — FNV-1a on the row key, never
 * `Math.random` (CLAUDE.md §2), so the same engagement lists the same rows every time.
 *
 * INTEGRATION POINT — both of these are platform reads (docs/INTEGRATION-POINTS.md §6).
 * Activities come from the activation store; reports come from the document management
 * system, which also decides what report TYPES exist (NEW-60). The SHAPES here are the
 * real ones: `ActivityRecord` matches the supplied Activation Structure export column for
 * column, and `ReportRecord` matches the report register the platform already renders
 * elsewhere.
 *
 * The inventory scores coverage. These lists are what it scored — a number nobody can
 * open is a number nobody can check, which is why every category is now clickable.
 */

/** FNV-1a → unit float. Same key, same value, everywhere, for ever. */
function unit(key: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h / 0xffffffff;
}

const pick = <T>(list: readonly T[], key: string): T => {
  const value = list[Math.floor(unit(key) * list.length)] ?? list[0];
  if (value === undefined) throw new Error('pick: empty list.');
  return value;
};

/** Column for column, the Activation Structure export. */
export interface ActivityRecord {
  readonly sponsorshipId: string;
  readonly sponsorshipName: string;
  readonly brandId: string;
  readonly brandName: string;
  readonly nationalRegional: string;
  readonly geo: string;
  readonly activityId: string;
  readonly activityName: string;
  readonly tactic: string;
  readonly subTactic: string;
  readonly activity: string;
  readonly activityMedium: string;
  readonly touchpoint: string;
  readonly asset: string;
  readonly item: string;
  readonly metric: string;
  readonly value: number;
  readonly currency: string;
  readonly startDate: string;
  readonly endDate: string;
  readonly source: string;
  readonly dataQuality: number;
  readonly dqsMethod: string;
}

/**
 * Tactic → the sub-tactics that can sit under it. Paired rather than picked
 * independently: "Experiential / Media" is not a combination that exists, and a table of
 * impossible pairs is a table nobody trusts even when every other column is right.
 */
const TACTIC_TREE: { tactic: string; subs: readonly string[] }[] = [
  { tactic: 'Sponsorship', subs: ['Media', 'Experiental'] },
  { tactic: 'Media', subs: ['Media'] },
  { tactic: 'POS', subs: ['Merch / Giveaways'] },
  { tactic: 'Experiential', subs: ['Experiental'] },
  { tactic: 'Sweepstakes', subs: ['Merch / Giveaways'] },
];
const ACTIVITIES = [
  'Linear TV',
  'Streaming / Digital TV',
  'Radio',
  'Social Media',
  'Online PR',
  'Print PR',
  'Digital',
  'OOH - In Venue',
  'OOH - Out of Venue',
  'Event',
] as const;
const MEDIUMS = ['Partner Owned', 'Partner Paid', 'Brand Paid', 'Earned'] as const;
const TOUCHPOINTS = [
  'On-Field/Court Signage (Physical)',
  'On-Field/Court Signage (Digital)',
  'Sideline / Backstop Signage',
  'News and blogs',
  'Jersey Patch',
  'Concourse Signage',
  'FanZone Activation',
  'VIP Private Gathering',
] as const;
const METRICS = [
  '100_smv',
  'vsmv',
  'impressions',
  'real_impressions',
  'quality_impressions',
  'reach',
  'attendance',
] as const;
const SOURCES = ['Partnership Net', 'Nielsen Sports', 'Partner', 'Cogent'] as const;
const DQS_METHODS = [
  'Uncalibrated transformation',
  'Calibrated transformation',
  'Direct measurement',
] as const;

export interface ActivityInput {
  readonly contracts: readonly {
    readonly id: string;
    readonly name: string;
    readonly rightsHolder: string;
    readonly brand: string;
    readonly geo: string;
  }[];
  readonly years: readonly string[];
  /** Visibility channels the partner map confirmed, per contract id. */
  readonly channelsFor: (contractId: string) => readonly string[];
}

/**
 * Rows per contract per year. Deliberately modest — this list is for CHECKING, and the
 * real store holds a hundred thousand rows the platform pages through. The counts the
 * inventory reports come from `activityCounts`, not from this array's length, so the
 * displayed totals stay honest while the table stays openable.
 */
const ROWS_PER_CONTRACT_YEAR = 6;

export function buildActivities(input: ActivityInput): ActivityRecord[] {
  const out: ActivityRecord[] = [];
  for (const c of input.contracts) {
    const channels = input.channelsFor(c.id);
    for (const year of input.years) {
      for (let i = 0; i < ROWS_PER_CONTRACT_YEAR; i++) {
        const key = `${c.id}|${year}|${String(i)}`;
        const activity = channels.length
          ? (channels[i % channels.length] ?? pick(ACTIVITIES, key))
          : pick(ACTIVITIES, key);
        const medium = pick(MEDIUMS, `m${key}`);
        const month = 1 + Math.floor(unit(`mo${key}`) * 12);
        const day = 1 + Math.floor(unit(`d${key}`) * 27);
        const date = `${year}-${month < 10 ? '0' : ''}${String(month)}-${day < 10 ? '0' : ''}${String(day)}`;
        const metric = pick(METRICS, `me${key}`);
        const branch = pick(TACTIC_TREE, `t${key}`);
        out.push({
          sponsorshipId: c.id.replace(/\D/g, '').slice(0, 5) || '00000',
          sponsorshipName: c.rightsHolder,
          brandId: String(13849 + Math.floor(unit(`b${c.brand}`) * 6)),
          brandName: c.brand,
          nationalRegional: c.geo === '(country)' ? 'National' : 'Regional - DMA',
          geo: c.geo,
          activityId: `${c.id}_${activity.replace(/\s+/g, '')}_${medium.replace(/\s+/g, '')}`,
          activityName: `${c.brand}_${c.rightsHolder}_${activity}`,
          tactic: branch.tactic,
          subTactic: pick(branch.subs, `s${key}`),
          activity,
          activityMedium: medium,
          touchpoint: pick(TOUCHPOINTS, `tp${key}`),
          asset: '',
          item: '',
          metric,
          value: Math.round(unit(`v${key}`) * 48_000 * 100) / 100,
          currency: 'USD',
          startDate: date,
          endDate: date,
          source: pick(SOURCES, `so${key}`),
          dataQuality: Math.round(45 + unit(`q${key}`) * 52),
          dqsMethod: pick(DQS_METHODS, `dm${key}`),
        });
      }
    }
  }
  return out;
}

/**
 * What the store HOLDS, as opposed to what the table draws. A real activation store
 * returns tens of thousands of rows per year; the inventory reports that figure, and the
 * table pages through a readable sample of it.
 */
export function activityCounts(
  input: ActivityInput,
): { year: string; rows: number; contracts: number }[] {
  return input.years.map((year) => {
    let rows = 0;
    for (const c of input.contracts) {
      const channels = Math.max(1, input.channelsFor(c.id).length);
      // Deterministic per contract-year, and shaped like real activation volume: more
      // channels, more rows, with a spread wide enough that a year-on-year change means
      // something.
      rows += Math.round(channels * (180 + unit(`cnt${c.id}${year}`) * 620));
    }
    return { year, rows, contracts: input.contracts.length };
  });
}

// ── Reports ──────────────────────────────────────────────────────────────────

export type ReportAccess = 'Public' | 'Restricted';

export interface ReportRecord {
  readonly id: string;
  readonly title: string;
  /** Nielsen, YouGov, Partnership Net… — who produced it. */
  readonly provider: string;
  readonly category: string;
  /** 0–100. The provider's own quality rating, not our DCS. */
  readonly quality: number;
  readonly access: ReportAccess;
  readonly brand: string;
  /** Rights holder the report is about; empty for a general market report. */
  readonly rightsHolder: string;
  readonly date: string;
  readonly sizeMb: number;
  readonly teams: number;
  /** Which inventory layer it counts towards. */
  readonly layer: 'recap' | 'cogent' | 'event' | 'contract';
}

const REPORT_TEMPLATES: {
  title: string;
  provider: string;
  category: string;
  layer: ReportRecord['layer'];
}[] = [
  { title: 'Media Value Report', provider: 'Nielsen', category: 'Media Impact', layer: 'recap' },
  {
    title: 'Sponsorship ROI Benchmark Study',
    provider: 'Nielsen',
    category: 'Benchmarking',
    layer: 'recap',
  },
  {
    title: 'Sponsorship Tracking Report',
    provider: 'YouGov',
    category: 'Brand Tracking',
    layer: 'recap',
  },
  { title: 'Brand Equity Wave', provider: 'Kantar', category: 'Brand Equity', layer: 'recap' },
  {
    title: 'Social Listening Recap',
    provider: 'Brandwatch',
    category: 'Social Media',
    layer: 'cogent',
  },
  {
    title: 'Experiential Activation Recap',
    provider: 'Partner',
    category: 'Industry Research',
    layer: 'cogent',
  },
  {
    title: 'Event Attendance Certification',
    provider: 'Partner',
    category: 'Research',
    layer: 'event',
  },
  {
    title: 'Rights Delivery Statement',
    provider: 'Partner',
    category: 'Market Intelligence',
    layer: 'contract',
  },
];

export interface ReportInput {
  readonly contracts: readonly {
    readonly id: string;
    readonly name: string;
    readonly rightsHolder: string;
    readonly brand: string;
  }[];
  readonly years: readonly string[];
}

export function buildReports(input: ReportInput): ReportRecord[] {
  const out: ReportRecord[] = [];
  for (const c of input.contracts) {
    for (const year of input.years) {
      const key = `${c.id}|${year}`;
      // Not every contract has every report — that gap is the point of the inventory.
      const n = Math.floor(unit(`n${key}`) * 3);
      // Distinct templates per contract-year. Hashing the index independently produced
      // the same report twice for the same partner and year, which reads as a duplicate
      // in the register — the one thing a document list must never invent.
      const offset = Math.floor(unit(`o${key}`) * REPORT_TEMPLATES.length);
      for (let i = 0; i < n; i++) {
        const t = REPORT_TEMPLATES[(offset + i) % REPORT_TEMPLATES.length];
        if (t === undefined) continue;
        const month = 1 + Math.floor(unit(`m${key}${String(i)}`) * 12);
        out.push({
          id: `${c.id}-${year}-${String(i)}`,
          title: `${t.title} — ${c.rightsHolder} ${year}`,
          provider: t.provider,
          category: t.category,
          quality: Math.round(72 + unit(`q${key}${String(i)}`) * 26),
          access: unit(`a${key}${String(i)}`) > 0.45 ? 'Restricted' : 'Public',
          brand: c.brand,
          rightsHolder: c.rightsHolder,
          date: `${year}-${month < 10 ? '0' : ''}${String(month)}-${String(1 + Math.floor(unit(`d${key}${String(i)}`) * 27)).padStart(2, '0')}`,
          sizeMb: Math.round((0.6 + unit(`s${key}${String(i)}`) * 4.2) * 10) / 10,
          teams: 1 + Math.floor(unit(`tm${key}${String(i)}`) * 3),
          layer: t.layer,
        });
      }
    }
  }
  // A general market report belongs to no contract, and the list has to show that a
  // report can exist without one.
  out.push({
    id: 'general-benchmark',
    title: 'Sponsorship ROI Benchmark Study 2025',
    provider: 'Nielsen',
    category: 'Benchmarking',
    quality: 92,
    access: 'Public',
    brand: '',
    rightsHolder: '',
    date: `${input.years[0] ?? '2024'}-01-15`,
    sizeMb: 1.8,
    teams: 0,
    layer: 'recap',
  });
  return out.sort((a, b) => b.date.localeCompare(a.date));
}

// ── The inventory register ───────────────────────────────────────────────────

/**
 * The assessment rows, built from the SAME pull the lists above describe.
 *
 * The inventory used to open empty and report "0 partners" on a step whose own copy says
 * Processing pre-filled it. Two surfaces disagreeing about whether the work happened is
 * worse than either answer alone.
 *
 * Cell states are derived from what the report list actually contains for that contract
 * and period, so the coverage score and the openable list cannot drift: a partner with a
 * recap report on file scores a recap cell, and one without it scores `unknown` —
 * nothing reported, which is not the same as confirmed unavailable.
 */
export interface RegisterRow {
  readonly id: string;
  readonly name: string;
  /** `layer:period` → state letter. Consumed by the caller, which owns the Partner type. */
  readonly cells: Record<string, 'received' | 'partial' | 'expected' | 'none' | 'na' | 'unknown'>;
  readonly obs: string;
}

export function buildRegister(
  input: ReportInput,
  reports: readonly ReportRecord[],
  periods: readonly string[],
): RegisterRow[] {
  const byContract = new Map<string, ReportRecord[]>();
  for (const c of input.contracts) byContract.set(c.id, []);
  for (const r of reports) {
    const owner = input.contracts.find((c) => c.rightsHolder === r.rightsHolder);
    if (owner) byContract.get(owner.id)?.push(r);
  }

  return input.contracts.map((c) => {
    const mine = byContract.get(c.id) ?? [];
    const cells: RegisterRow['cells'] = {};
    periods.forEach((period, pi) => {
      const year = input.years[pi] ?? period;
      const forYear = mine.filter((r) => r.date.startsWith(year));
      // Contract: a signed deal is on file for every period it covers. The exception is
      // a contract the register itself never resolved, which stays unknown.
      cells[`contract:${period}`] = unit(`ct${c.id}${period}`) > 0.08 ? 'received' : 'unknown';
      const recap = forYear.find((r) => r.layer === 'recap');
      cells[`recap:${period}`] = recap
        ? recap.quality >= 85
          ? 'received'
          : 'partial'
        : unit(`rc${c.id}${period}`) > 0.72
          ? 'expected'
          : 'unknown';
      const cogent = forYear.find((r) => r.layer === 'cogent');
      cells[`cogent:${period}`] = cogent
        ? 'received'
        : unit(`cg${c.id}${period}`) > 0.82
          ? 'none'
          : 'unknown';
      const event = forYear.find((r) => r.layer === 'event');
      // A period a partner cannot have — a deal that had not started — leaves the
      // denominator rather than scoring zero (CLAUDE.md §6).
      cells[`event:${period}`] = event
        ? 'received'
        : unit(`ev${c.id}${period}`) > 0.9
          ? 'na'
          : 'unknown';
    });
    return {
      id: c.id,
      name: c.rightsHolder,
      cells,
      obs: mine.length === 0 ? 'Nothing on file for this contract in the analysis window.' : '',
    };
  });
}
